import styled from "styled-components"
import React, { useState, useEffect, useMemo } from "react";
import { ListContainer, Error } from './styles'
import { useAuth } from "../AuthContext";
import axios from "axios";
import { baseUrl } from "../config";
import { apiDateToDate, formatEventDateLocal } from "../utils/dateConverters";
import { BiSolidUpvote, BiSolidDownvote } from "react-icons/bi";
import { MaterialReactTable } from 'material-react-table';
import { Dialog, DialogTitle, DialogContent, List, ListItemButton, ListItemText } from "@mui/material";
import CategoryTagsCell from "./CategoryTagsCell";
import NomCellContent from "./NomCellContent";
import linkDisplayLabel from "../utils/linkDisplayLabel";

/** Maps API sortKey to MRT column id (accessorKey). */
function eventSortKeyToColumnId(key) {
    if (key === "Date") return "date";
    return key;
}

const EventsList = ({
    fetchEvents,
    events,
    sortConfig,
    setSortConfig,
    DateIRLToErache,
    rowCount,
    setPagination,
    pagination,
    setColumnFilters,
    columnFilters,
    columnVisibility,
    setColumnVisibility,
    onOpenEventDetail,
}) => {

    const { isLoggedIn } = useAuth();
    const [error, setError] = useState("");
    const [linksPopup, setLinksPopup] = useState(null);

    const onVote = async (eventId, inc) => {
        try {
            setError("");
            await axios.post(
                baseUrl + `/events/${eventId}/vote`,
                {
                    inc: inc
                },
                { withCredentials: true }
            );
        } catch (err) {
            if (err.response) {
                if (err.response.status === 401) {
                    setError("Vous devez être connecté pour voter.");
                } else if (err.response.status === 403) {
                    setError("Vous n'avez pas le droit de voter pour cet évènement.");
                } else {
                    setError("Erreur lors du vote. Veuillez réessayer plus tard.");
                }
            }
        }
        fetchEvents(pagination.pageIndex, pagination.pageSize, sortConfig.key, sortConfig.direction, columnFilters);
    };

    const sorting = useMemo(
        () => [{ id: eventSortKeyToColumnId(sortConfig.key), desc: sortConfig.direction === "desc" }],
        [sortConfig.key, sortConfig.direction]
    );

    const [groupedOptions, setGroupedOptions] = useState([]);
    useEffect(() => {
        axios.get(baseUrl + "/event-categories")
            .then(res => {
                const options = res.data.map(cat => ({
                    label: cat.name,
                    value: cat.id
                }));
                setGroupedOptions(options);
            })
            .catch(err => {
                setError("Erreur lors de la récupération des catégories: " + err.message);
            });
    }, []);

    const columns = [
        {
            accessorKey: 'dateErache',
            header: 'Erache',
            Cell: ({ row }) => DateIRLToErache(apiDateToDate(row.original.date)),
            enableColumnFilter: false,
        },
        {
            accessorKey: 'date',
            header: 'Date IRL',
            size: 140,
            filterVariant: 'date-range',
            Cell: ({ cell }) => formatEventDateLocal(cell.getValue()),
            enableColumnFilter: false,
        },
        {
            accessorKey: "name",
            header: "Nom",
            size: 360,
            grow: 1,
            muiTableBodyCellProps: {
                sx: {
                    whiteSpace: "nowrap",
                    alignItems: "center",
                    verticalAlign: "middle",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                },
            },
            Cell: ({ row }) => <NomCellContent>{row.original.name}</NomCellContent>,
        },
        { accessorKey: 'rpDescription', header: 'Description RP' },
        { accessorKey: 'hrpDescription', header: 'Description HRP' },
        {
            accessorKey: 'links',
            header: '🔗',
            filterVariant: 'select',
            filterSelectOptions: [
                { label: 'Tous', value: '' },
                { label: 'Avec lien', value: 'has' },
                { label: 'Sans lien', value: 'none' },
            ],
            size: 50,
            Cell: ({ row }) => {
                const links = row.original.links || [];
                if (links.length === 0) return "—";
                const stop = (e) => e.stopPropagation();
                if (links.length === 1) {
                    return (
                        <span onClick={stop}>
                            <a href={links[0]} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }} onClick={stop}>🔗</a>
                        </span>
                    );
                }
                return (
                    <span onClick={stop}>
                        <LinkButton type="button" onClick={(e) => { e.stopPropagation(); setLinksPopup({ eventName: row.original.name, links }); }} title={`${links.length} liens`}>
                            🔗
                        </LinkButton>
                    </span>
                );
            },
        },
        {
            accessorKey: 'categories',
            grow: 1,
            size: 250,
            header: 'Catégories',
            filterVariant: 'multi-select',
            filterSelectOptions: groupedOptions,
            Cell: ({ cell }) => {
                const categories = cell.getValue();
                if (!categories || categories.length === 0) return "N/A";
                return <CategoryTagsCell categories={categories} singleLine />;
            }
        },
        { accessorKey: 'authorPseudo', header: 'Auteur' },
        {
            size: 90,
            accessorKey: 'votes',
            header: 'Votes',
            enableColumnFilter: false,
            Cell: ({ row }) =>
                isLoggedIn ? (
                    <VoteContainer onClick={(e) => e.stopPropagation()}>
                        <BorderlessButton onClick={() => onVote(row.original.id, 1)}>
                            <BiSolidUpvote size={20} color={row.original.userVote === 1 ? "orange" : "grey"} />
                        </BorderlessButton>
                        <VoteCount>{row.original.voteCount || 0}</VoteCount>
                        <BorderlessButton onClick={() => onVote(row.original.id, -1)}>
                            <BiSolidDownvote size={20} color={row.original.userVote === -1 ? "blue" : "grey"} />
                        </BorderlessButton>
                    </VoteContainer>
                ) : null
        },
    ];



    return (
        <EventsListContainer>
            {error && <Error>{error}</Error>}
            <Dialog open={!!linksPopup} onClose={() => setLinksPopup(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Liens — {linksPopup?.eventName}</DialogTitle>
                <DialogContent>
                    <List>
                        {(linksPopup?.links || []).map((url, i) => (
                            <ListItemButton key={i} component="a" href={url} target="_blank" rel="noopener noreferrer">
                                <ListItemText primary={linkDisplayLabel(url)} secondary={url} primaryTypographyProps={{ noWrap: true }} />
                            </ListItemButton>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>
            <TableWrapper>
                <MaterialReactTable
                    columns={columns}
                    data={events}
                    muiTableBodyRowProps={
                        onOpenEventDetail
                            ? ({ row }) => ({
                                  onClick: () => onOpenEventDetail(row.original.id),
                                  sx: {
                                      cursor: "pointer",
                                      backgroundColor: "var(--bg)",
                                      "&:hover": { backgroundColor: "var(--bg)" },
                                  },
                              })
                            : undefined
                    }
                    enableColumnOrdering
                    enableColumnResizing
                    enableSorting
                    enableColumnActions={false}
                    enableHiding
                    manualPagination // server side pagination
                    manualFiltering // server side filtering
                    manualSorting // server side sort (Erache / votes are not row fields; avoid client-only sort)
                    rowCount={rowCount} // nombre total de lignes pour MRT
                    state={{
                        pagination,
                        columnFilters,
                        columnVisibility,
                        sorting,
                    }}
                    onColumnFiltersChange={setColumnFilters}
                    onColumnVisibilityChange={setColumnVisibility}
                    onPaginationChange={setPagination}
                    onSortingChange={(updater) => {
                        setSortConfig((prev) => {
                            const prevSorting = [
                                { id: eventSortKeyToColumnId(prev.key), desc: prev.direction === "desc" },
                            ];
                            const next = typeof updater === "function" ? updater(prevSorting) : updater;
                            if (!next || next.length === 0) {
                                return { key: "date", direction: "asc" };
                            }
                            const { id, desc } = next[0];
                            return { key: id, direction: desc ? "desc" : "asc" };
                        });
                    }}
                    muiTableContainerProps={{
                        sx: { minWidth: '100%', width: '100%', backgroundColor: 'var(--bg)' }
                    }}
                    muiTablePaperProps={{
                        elevation: 0,
                        sx: {
                            width: '100%',
                            maxWidth: '100%',
                            backgroundColor: 'var(--bg)',
                        },
                    }}
                    muiTableProps={{
                        sx: { minWidth: '100%', width: '100%', backgroundColor: 'var(--bg)' }
                    }}

                    initialState={{
                        density: 'compact',
                    }}
                    muiTopToolbarProps={{
                        sx: {
                            backgroundColor: 'var(--bg)',
                            color: 'var(--text-muted)',
                            '& .MuiButtonBase-root': { color: 'inherit' },
                        },
                    }}
                    muiBottomToolbarProps={{
                        sx: {
                            backgroundColor: 'var(--bg)',
                            color: 'var(--text-muted)',
                            '& .MuiButtonBase-root': { color: 'inherit' },
                        },
                    }}
                    muiTableBodyCellProps={{
                        sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
                    }}
                    muiTableHeadCellProps={{
                        sx: { verticalAlign: 'middle', backgroundColor: 'var(--bg)' },
                    }}
                    muiTableHeadRowProps={{
                        sx: { backgroundColor: 'var(--bg)' },
                    }}
                />
            </TableWrapper>
        </EventsListContainer>
    );
};
export default EventsList;

const EventsListContainer = styled(ListContainer)`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  box-sizing: border-box;
  padding: 0 10px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
`;

const BorderlessButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  outline: none;
`;

const VoteContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const VoteCount = styled.span`
  min-width: 20px; 
  text-align: center;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 1em;
  &:hover { opacity: 0.8; }
`;
